/**
 * Auth user context and hook. `useAuthUser` merges the react-query user from
 * AuthUserProvider with redux channel/territory preferences and a realm
 * derived from pathname + role via `getRealm`.
 *
 * Lifecycle position: AuthUserProvider (context) → useAuthUser (consumers) → …
 */
import { createContext, useContext } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { getRealm } from "@features/realms/realmRouting";
import { AuthUser } from "@models/AuthUser";

export const AuthUserProviderContext = createContext<AuthUser | null>(null);

export const useOptionalAuthUser = () => {
  const { pathname } = useLocation();
  const { channel, territory } = useSelector(
    (state: any) => ({
      channel: state.app.channel as string,
      territory: state.app.territory as string,
    }),
    shallowEqual
  );
  const context = useContext(AuthUserProviderContext);
  if (!context) {
    return null;
  }

  const realm = getRealm(pathname, context.role);

  return { ...context, channel, territory, realm };
};

export const useAuthUser = () => {
  const user = useOptionalAuthUser();
  if (!user) {
    throw new Error("useAuthUser must be used within a AuthUserProvider");
  }
  return user;
};
